import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TerminalesPanel } from '../components/TerminalesPanel';
import { MyTimePanel } from '../components/MyTimePanel';
import { SubcontratasPanel } from '../components/SubcontratasPanel';
import { toast } from 'sonner';

// ServerInfoPanel: muestra la IP de salida REAL del pod y si RDS es
// alcanzable, consultando /api/sync/outbound-ip a demanda (botón "Consultar").
// Antes este bloque mostraba una IP fija hardcodeada en el código — quedaba
// desactualizada en cada redeploy (la IP de salida del pod cambia) e
// inducía a error al intentar whitelistarla en el Security Group de AWS RDS.
// NOTA: se consulta solo a demanda (no auto al montar) para no competir con
// el resto de peticiones/polling de la página nada más entrar a la pestaña.
function ServerInfoPanel() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const stored = localStorage.getItem('smc_token');
      const r = await fetch(`${backendUrl}/api/sync/outbound-ip`, {
        headers: stored ? { Authorization: `Bearer ${stored}` } : {},
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error consultando la IP de salida');
      setInfo(j);
    } catch (e) {
      setError(e.message || 'Error consultando la IP de salida');
    }
    setLoading(false);
  };

  const rows = [
    { label: 'IP de salida (pod)', value: loading ? 'Consultando…' : (info ? (info.outbound_ip || '—') : 'Pulsa "Consultar" →') },
    { label: 'RDS Host', value: info?.rds_host || 'valorizaclockin.ctmskawqujme.eu-north-1.rds.amazonaws.com' },
    { label: 'RDS alcanzable', value: loading ? '…' : (info ? (info.rds_reachable ? 'Sí' : 'No') : '—') },
    { label: 'Base de datos', value: 'ValorizaClockIn' },
    { label: 'Zona horaria', value: 'Europe/Madrid (DST auto)' },
  ];

  return (
    <div style={{ padding: '14px 16px', background: '#0f172a', borderRadius: '8px', border: '1px solid #334155' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
        <div style={{ color: '#64748b', fontSize: '12px' }}>Información del servidor</div>
        <button
          data-testid="server-info-refresh"
          onClick={load}
          disabled={loading}
          style={{ background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: '6px', padding: '2px 10px', fontSize: '11px', cursor: loading ? 'default' : 'pointer' }}
        >
          {loading ? 'Consultando…' : (info ? '↻ Actualizar' : 'Consultar IP de salida')}
        </button>
      </div>
      {error && (
        <div data-testid="server-info-error" style={{ color: '#fca5a5', fontSize: '12px', marginBottom: '6px' }}>{error}</div>
      )}
      {!loading && info && !info.rds_reachable && (
        <div style={{ color: '#fbbf24', fontSize: '12px', marginBottom: '6px' }}>
          ⚠ RDS no es alcanzable con esta IP. Si acabas de desplegar, añade la IP de salida al Security Group de AWS RDS (puerto 5432).
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {rows.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '12px' }}>
            <span style={{ color: '#475569', fontSize: '12px', minWidth: '140px' }}>{item.label}:</span>
            <code data-testid={`server-info-${i}`} style={{ color: '#94a3b8', fontSize: '12px', fontFamily: 'IBM Plex Mono' }}>{item.value}</code>
          </div>
        ))}
      </div>
    </div>
  );
}

// BackfillCard: admin-only control to re-sync N days of historical punches from RDS
function BackfillCard() {
  const { apiFetch, user } = useAuth();
  const [days, setDays] = useState(90);
  const [busy, setBusy] = useState(false);
  if (user?.role !== 'admin') return null;
  const run = async () => {
    if (!window.confirm(`¿Lanzar backfill de los últimos ${days} días? Esto procesará fichajes de RDS y puede tardar varios minutos.`)) return;
    setBusy(true);
    try {
      const r = await apiFetch(`/api/sync/backfill?days=${days}`, { method: 'POST' });
      const j = await r.json();
      if (r.ok) toast.success(j.message || 'Backfill iniciado', { duration: 8000 });
      else      toast.error(j.detail || 'Error al lanzar backfill');
    } catch (e) { toast.error('Error al lanzar backfill'); }
    setBusy(false);
  };
  return (
    <div style={{ padding: '14px 16px', background: '#0f172a', borderRadius: '8px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 500 }}>Backfill histórico</div>
        <div style={{ color: '#64748b', fontSize: '12px' }}>
          Reprocesa los fichajes de RDS para un rango histórico. Úsalo tras configurar un cliente nuevo o para rellenar huecos.
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {[30, 90, 180, 365].map(n => (
          <button
            key={n}
            onClick={() => setDays(n)}
            data-testid={`backfill-preset-${n}`}
            style={{
              padding: '6px 14px',
              background: days === n ? '#60a5fa' : '#273449',
              color: days === n ? '#0f172a' : '#f1f5f9',
              border: `1px solid ${days === n ? '#60a5fa' : '#334155'}`,
              borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: days === n ? 600 : 400,
            }}>{n} días</button>
        ))}
        <Button
          onClick={run}
          disabled={busy}
          data-testid="backfill-run"
          style={{ background: '#22c55e', color: '#052e16', marginLeft: 'auto', fontWeight: 600 }}
        >
          {busy ? 'Lanzando…' : `↻ Reprocesar ${days} días`}
        </Button>
      </div>
    </div>
  );
}

export default function ConfiguracionPage() {
  const { apiFetch } = useAuth();
  const { t } = useLang();
  const [config, setConfig]   = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [driveStatus, setDriveStatus] = useState({ loading: true });

  useEffect(() => {
    setLoading(true);
    apiFetch('/api/config').then(r => r.json()).then(c => setConfig(c)).catch(() => {}).finally(() => setLoading(false));
  }, [apiFetch]);

  // Load Google Drive OAuth status
  const loadDriveStatus = async () => {
    try {
      const res = await apiFetch('/api/oauth/drive/status');
      const data = await res.json();
      setDriveStatus({ loading: false, ...data });
    } catch (err) {
      setDriveStatus({ loading: false, configured: false, error: err.message });
    }
  };
  useEffect(() => { loadDriveStatus(); }, [apiFetch]);

  // If we just came back from Drive OAuth, refresh and show toast
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected') === 'true') {
      toast.success('Google Drive conectado correctamente');
      window.history.replaceState({}, '', window.location.pathname);
      loadDriveStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectDrive = async () => {
    try {
      const res = await apiFetch('/api/oauth/drive/connect');
      const data = await res.json();
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error('No se pudo generar la URL de autorización');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm('¿Seguro que quieres desconectar Google Drive? El sync volverá a usar solo SFTP.')) return;
    try {
      await apiFetch('/api/oauth/drive/disconnect', { method: 'POST' });
      toast.success('Google Drive desconectado');
      loadDriveStatus();
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const testDrive = async () => {
    try {
      const res = await apiFetch('/api/oauth/drive/test');
      const data = await res.json();
      if (data.ok) {
        toast.success(`✓ Drive OK — ${data.count} CSVs recientes: ${data.files.map(f => f.name).join(', ')}`);
      } else {
        toast.error('Test falló');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const save = async () => {
    // Client-side validation: night_start == night_end is invalid
    const ns = config.night_start_hour, ne = config.night_end_hour;
    if (ns !== undefined && ne !== undefined && ns === ne) {
      toast.error('El inicio y fin del turno noche no pueden ser iguales. Ejemplo válido para Briviesca: Inicio noche 22 / Fin noche 6.');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch('/api/config', { method: 'PUT', body: JSON.stringify(config) });
      if (res.ok) toast.success('Configuración guardada');
      else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Error al guardar');
      }
    } catch { toast.error('Error de conexión'); }
    setSaving(false);
  };

  const [reclassifying, setReclassifying] = useState(false);
  const reclassify = async () => {
    if (!window.confirm('Esto re-aplicará la configuración actual (turnos y auto-cierre) a TODOS los fichajes guardados. Es seguro y no consulta RDS. ¿Continuar?')) return;
    setReclassifying(true);
    try {
      // Save first to be sure latest values are persisted before reclass
      await apiFetch('/api/config', { method: 'PUT', body: JSON.stringify(config) });
      const res = await apiFetch('/api/kpis/reclassify', { method: 'POST' });
      let data = null;
      try {
        data = await res.json();
      } catch (_jsonErr) {
        // Body unparseable — likely empty due to a 5xx or interceptor edge-case
        data = null;
      }
      if (!res.ok) {
        toast.error(`Error ${res.status}${data?.detail ? ': ' + data.detail : ' al reclasificar'}`);
      } else if (data && data.ok) {
        toast.success(`✓ Reclasificados ${(data.updated || 0).toLocaleString('es-ES')} fichajes (${data.shifts_changed || 0} turnos, ${data.hours_changed || 0} horas)`);
      } else {
        toast.error('Error al reclasificar');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
    setReclassifying(false);
  };

  const set = (key, val) => setConfig(c => ({ ...c, [key]: val }));

  const Field = ({ label, field, type = 'text', placeholder = '' }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Label style={{ color: '#94a3b8', fontSize: '13px' }}>{label}</Label>
      <Input
        data-testid={`settings-${field}`}
        type={type}
        value={config[field] || ''}
        onChange={e => set(field, e.target.value)}
        placeholder={placeholder}
        style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', fontFamily: type === 'text' && field.includes('id') ? 'IBM Plex Mono' : 'inherit' }}
      />
    </div>
  );

  const handleDownloadManual = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manual/download`);
      if (!res.ok) throw new Error('No se pudo descargar el manual');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Manual_Usuario_SmartClockIn.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando manual:', err);
      // Fallback: abrir en pestaña nueva
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/manual/download`, '_blank');
    }
  };

  return (
    <div style={{ padding: '32px', overflowY: 'auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '24px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>{t('configuracion')}</h1>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>Ajustes del sistema SmartClockIn</p>
      </div>


      {/* Google Drive Integration */}
      <motion.div
        data-testid="gdrive-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontFamily: 'Space Grotesk', fontSize: '18px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
              📁 Google Drive (fallback de empleados)
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '4px', marginBottom: 0 }}>
              Cuando el SFTP corporativo no esté accesible, el backend descarga el CSV diario de empleados desde esta carpeta de Drive.
            </p>
          </div>
          {driveStatus.loading ? (
            <span style={{ color: '#64748b', fontSize: '13px' }}>Cargando…</span>
          ) : driveStatus.authorised ? (
            <span data-testid="gdrive-status-connected" style={{ padding: '4px 10px', background: '#052e16', border: '1px solid #22c55e', color: '#22c55e', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
              ✓ Conectado
            </span>
          ) : (
            <span data-testid="gdrive-status-disconnected" style={{ padding: '4px 10px', background: '#3b0a0a', border: '1px solid #ef4444', color: '#fca5a5', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
              ✗ No conectado
            </span>
          )}
        </div>

        {driveStatus.authorised && (
          <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '12px', color: '#94a3b8', fontFamily: 'IBM Plex Mono' }}>
            <div>Autorizado por: <span style={{ color: '#60a5fa' }}>{driveStatus.granted_by}</span></div>
            <div>Folder ID: <span style={{ color: '#60a5fa' }}>{driveStatus.folder_id}</span></div>
            <div>Scopes: <span style={{ color: '#60a5fa' }}>{(driveStatus.scopes || []).join(', ')}</span></div>
            {driveStatus.expires_at && <div>Token expira: <span style={{ color: '#60a5fa' }}>{driveStatus.expires_at}</span></div>}
            <div data-testid="gdrive-last-test-ok">Último test OK: <span style={{ color: '#4ade80' }}>{driveStatus.last_test_ok_at ? driveStatus.last_test_ok_at.slice(0, 19).replace('T', ' ') : '—'}</span></div>
            <div data-testid="gdrive-last-csv-read">Último CSV leído: <span style={{ color: '#4ade80' }}>{driveStatus.last_csv_read_at ? driveStatus.last_csv_read_at.slice(0, 19).replace('T', ' ') : 'todavía ninguno'}</span>{driveStatus.last_csv_name && ` (${driveStatus.last_csv_name})`}</div>
          </div>
        )}

        {driveStatus.configured && !driveStatus.authorised && driveStatus.last_test_error && (
          <div data-testid="gdrive-last-test-error" style={{ background: '#3b0a0a', border: '1px solid #ef4444', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '12px', color: '#fca5a5' }}>
            ⚠ El test en vivo falló: {driveStatus.last_test_error}. Puede que el token haya caducado o la cuenta lo haya revocado — reautoriza abajo.
          </div>
        )}

        {!driveStatus.configured && (
          <div style={{ background: '#3b0a0a', border: '1px solid #ef4444', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '13px', color: '#fca5a5' }}>
            ⚠ El backend no tiene configurado GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET. Contacta con el equipo técnico.
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {driveStatus.configured && !driveStatus.authorised && (
            <Button
              data-testid="gdrive-connect-btn"
              onClick={connectDrive}
              style={{ background: '#2563eb', color: 'white' }}
            >
              🔗 Conectar Google Drive
            </Button>
          )}
          {driveStatus.authorised && (
            <>
              <Button
                data-testid="gdrive-test-btn"
                onClick={testDrive}
                style={{ background: '#1e40af', color: 'white' }}
              >
                🔍 Probar acceso (listar CSVs)
              </Button>
              <Button
                data-testid="gdrive-reconnect-btn"
                onClick={connectDrive}
                style={{ background: 'transparent', border: '1px solid #334155', color: '#94a3b8' }}
              >
                ↻ Reautorizar
              </Button>
              <Button
                data-testid="gdrive-disconnect-btn"
                onClick={disconnectDrive}
                style={{ background: 'transparent', border: '1px solid #7f1d1d', color: '#fca5a5' }}
              >
                ✗ Desconectar
              </Button>
            </>
          )}
        </div>
      </motion.div>

      {/* Manual de Usuario Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          maxWidth: '700px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: 'Space Grotesk', fontSize: '18px', fontWeight: 600, color: '#f1f5f9', margin: 0, marginBottom: '8px' }}>
              📖 Manual de Usuario
            </h2>
            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px', lineHeight: '1.5' }}>
              Guía completa con instrucciones paso a paso: configuración de contratos, horas comprometidas, turnos, evaluación con IA y más.
            </p>
            
            <div style={{
              background: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: '#64748b', fontSize: '12px' }}>📄</span>
                <span style={{ color: '#94a3b8', fontSize: '12px' }}>19 páginas</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: '#64748b', fontSize: '12px' }}>📸</span>
                <span style={{ color: '#94a3b8', fontSize: '12px' }}>15 capturas</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: '#64748b', fontSize: '12px' }}>💾</span>
                <span style={{ color: '#94a3b8', fontSize: '12px' }}>894 KB</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: '#64748b', fontSize: '12px' }}>📝</span>
                <span style={{ color: '#94a3b8', fontSize: '12px' }}>PDF</span>
              </div>
            </div>
            
            <Button
              onClick={handleDownloadManual}
              data-testid="download-manual-button"
              style={{
                background: '#2563eb',
                color: 'white',
                gap: '8px',
                display: 'flex',
                alignItems: 'center',
                padding: '10px 20px',
                width: '100%',
                justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              📥 Descargar Manual de Usuario
            </Button>
          </div>
        </div>
      </motion.div>

      <Tabs defaultValue="azure" style={{ maxWidth: '1100px' }}>
        <TabsList style={{ background: '#1e293b', border: '1px solid #334155', marginBottom: '20px' }}>
          <TabsTrigger value="azure" style={{ data: 'true' }}>Azure SSO</TabsTrigger>
          <TabsTrigger value="sftp">SFTP</TabsTrigger>
          <TabsTrigger value="kpi">Motor KPI</TabsTrigger>
          <TabsTrigger value="terminales">Terminales</TabsTrigger>
          <TabsTrigger value="mytime">MyTime</TabsTrigger>
          <TabsTrigger value="subcontratas">Subcontratas</TabsTrigger>
          <TabsTrigger value="sistema">Sistema</TabsTrigger>
        </TabsList>

        {/* Azure SSO */}
        <TabsContent value="azure">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            <div>
              <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '16px', fontWeight: 600, color: '#f1f5f9', margin: '0 0 4px' }}>Microsoft Entra ID (Azure AD)</h3>
              <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>Configura el SSO de Azure para autenticación corporativa.</p>
            </div>

            <div style={{ background: '#0f172a', border: '1px solid #1e3a5f', borderRadius: '8px', padding: '12px 16px', fontSize: '12px', color: '#64748b', fontFamily: 'IBM Plex Mono' }}>
              Tenant ID: 9133aec2-7cdd-4b6f-a5cc-65e7eb1384ec
            </div>

            <Field
              label="Client ID (Application ID)"
              field="azure_client_id"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            />
            <Field
              label="Client Secret"
              field="azure_client_secret"
              type="password"
              placeholder="••••••••••••"
            />

            <div style={{ background: '#0f1929', border: '1px solid #1e3a5f', borderRadius: '8px', padding: '12px 16px' }}>
              <p style={{ color: '#60a5fa', fontSize: '13px', margin: '0 0 8px', fontWeight: 600 }}>URLs de redirección a registrar en Azure:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {['https://workday-intelligence.preview.emergentagent.com', 'https://smartclockin.valorizasm.com'].map(url => (
                  <code key={url} style={{ color: '#94a3b8', fontSize: '12px', fontFamily: 'IBM Plex Mono', background: '#0f172a', padding: '4px 8px', borderRadius: '4px' }}>{url}</code>
                ))}
              </div>
            </div>
          </motion.div>
        </TabsContent>

        {/* SFTP */}
        <TabsContent value="sftp">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            <div>
              <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '16px', fontWeight: 600, color: '#f1f5f9', margin: '0 0 4px' }}>Conexión SFTP</h3>
              <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>Servidor de altas y bajas de personal.</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: '16px' }}>
              <Field label="Host / IP" field="sftp_host" placeholder="4.212.12.25" />
              <Field label="Puerto" field="sftp_port" type="number" placeholder="22" />
            </div>
            <Field label="Usuario" field="sftp_user" placeholder="RRHHLoga3" />
            <Field label="Contraseña" field="sftp_pass" type="password" placeholder="••••••••" />

            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '12px 16px' }}>
              <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 4px', fontWeight: 600 }}>Ruta base del servidor:</p>
              <code style={{ color: '#64748b', fontSize: '12px', fontFamily: 'IBM Plex Mono' }}>/RRHH-Loga3/DEFAULT/DEFAULT_CUSTOMER/PRODUKTION_DAILY/&#123;date&#125;/M0001/00000</code>
              <p style={{ color: '#475569', fontSize: '11px', margin: '8px 0 0' }}>IP de salida del servidor: <strong style={{ color: '#60a5fa', fontFamily: 'IBM Plex Mono' }}>34.170.12.145</strong> (whitelist requerida)</p>
            </div>
          </motion.div>
        </TabsContent>

        {/* Motor KPI */}
        <TabsContent value="kpi">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            <div>
              <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '16px', fontWeight: 600, color: '#f1f5f9', margin: '0 0 4px' }}>Motor KPI</h3>
              <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>Parámetros del algoritmo de cálculo de jornadas.</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Inicio turno noche (hora)</Label>
                <input
                  data-testid="kpi-night-start"
                  type="number" min="0" max="23"
                  value={config.night_start_hour ?? 22}
                  onChange={e => set('night_start_hour', parseInt(e.target.value))}
                  style={{ width: '100%', marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', outline: 'none', fontFamily: 'IBM Plex Mono' }}
                />
              </div>
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Fin turno noche / inicio mañana (hora)</Label>
                <input
                  data-testid="kpi-night-end"
                  type="number" min="0" max="23"
                  value={config.night_end_hour ?? 6}
                  onChange={e => set('night_end_hour', parseInt(e.target.value))}
                  style={{ width: '100%', marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', outline: 'none', fontFamily: 'IBM Plex Mono' }}
                />
              </div>
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Fin turno mañana / inicio tarde (hora)</Label>
                <input
                  data-testid="kpi-day-end"
                  type="number" min="0" max="23"
                  value={config.day_end_hour ?? 14}
                  onChange={e => set('day_end_hour', parseInt(e.target.value))}
                  style={{ width: '100%', marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', outline: 'none', fontFamily: 'IBM Plex Mono' }}
                />
              </div>
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Horas auto-cierre</Label>
                <input
                  data-testid="kpi-autoclose"
                  type="number" min="1" max="16" step="0.5"
                  value={config.autoclose_hours ?? 8}
                  onChange={e => set('autoclose_hours', parseFloat(e.target.value))}
                  style={{ width: '100%', marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', outline: 'none', fontFamily: 'IBM Plex Mono' }}
                />
              </div>
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Gap mínimo entre turnos (horas)</Label>
                <input
                  data-testid="kpi-min-shift-gap"
                  type="number" min="1" max="6" step="0.5"
                  value={config.min_shift_gap_hours ?? 2}
                  onChange={e => set('min_shift_gap_hours', parseFloat(e.target.value))}
                  style={{ width: '100%', marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', outline: 'none', fontFamily: 'IBM Plex Mono' }}
                />
                <p style={{ color: '#64748b', fontSize: '11px', margin: '4px 0 0' }}>
                  Si entre dos fichajes hay un hueco ≥ este valor → se considera turno distinto.
                </p>
              </div>
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Ventana deduplicación (minutos)</Label>
                <input
                  data-testid="kpi-dedup-window"
                  type="number" min="0" max="60" step="1"
                  value={config.dedup_window_minutes ?? 10}
                  onChange={e => set('dedup_window_minutes', parseFloat(e.target.value))}
                  style={{ width: '100%', marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', outline: 'none', fontFamily: 'IBM Plex Mono' }}
                />
                <p style={{ color: '#64748b', fontSize: '11px', margin: '4px 0 0' }}>
                  Doble fichaje accidental: 2 marcas en &lt; este intervalo → el segundo se descarta. (0 = desactivado)
                </p>
              </div>
            </div>

            <div style={{ background: '#0f172a', border: '1px solid #1e3a5f', borderRadius: '8px', padding: '14px 16px' }}>
              <p style={{ color: '#60a5fa', fontSize: '13px', margin: '0 0 8px', fontWeight: 600 }}>Reglas activas:</p>
              {[
                `Noche: ${config.night_start_hour ?? 22}h → ${config.night_end_hour ?? 6}h | Mañana: ${config.night_end_hour ?? 6}h → ${config.day_end_hour ?? 14}h | Tarde: ${config.day_end_hour ?? 14}h → ${config.night_start_hour ?? 22}h`,
                `00:00-${config.night_end_hour ?? 6}:00 Madrid → svc_date = día anterior`,
                `Fichaje único → auto-cierre +${config.autoclose_hours ?? 8}h (±15 min jitter)`,
                `Multi-turno: separación ≥ ${config.min_shift_gap_hours ?? 2}h entre fichajes → turnos distintos`,
                `Dobles fichajes accidentales < ${config.dedup_window_minutes ?? 10} min → descartados`,
                'Salida en día siguiente (00:00-15:00) → corrección automática',
                'DST Europe/Madrid aplicado automáticamente',
              ].map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#34d399', flexShrink: 0 }} />
                  <span style={{ color: '#64748b', fontSize: '12px' }}>{r}</span>
                </div>
              ))}
            </div>

            <div style={{ background: '#1a1f2e', border: '1px solid #fbbf24', borderRadius: '8px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <p style={{ color: '#fbbf24', fontSize: '13px', margin: 0, fontWeight: 600 }}>⚙ Aplicar a fichajes existentes</p>
              <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>
                Cambiar la configuración arriba <strong>solo afecta a futuras sincronizaciones</strong>.
                Pulsa este botón para reclasificar los fichajes ya guardados con los valores actuales (no consulta RDS, tarda segundos).
              </p>
              <Button
                data-testid="kpi-reclassify-btn"
                onClick={reclassify}
                disabled={reclassifying}
                style={{ background: '#fbbf24', color: '#0f172a', fontWeight: 600, alignSelf: 'flex-start' }}
              >
                {reclassifying ? 'Reclasificando…' : 'Reclasificar fichajes existentes'}
              </Button>
            </div>
          </motion.div>
        </TabsContent>

        {/* Terminales */}
        <TabsContent value="terminales">
          <TerminalesPanel />
        </TabsContent>

        {/* MyTime (teletrabajo) */}
        <TabsContent value="mytime">
          <MyTimePanel />
        </TabsContent>

        {/* Subcontratas (importación desde Excel) */}
        <TabsContent value="subcontratas">
          <SubcontratasPanel />
        </TabsContent>

        {/* Sistema */}
        <TabsContent value="sistema">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            <div>
              <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '16px', fontWeight: 600, color: '#f1f5f9', margin: '0 0 4px' }}>Sistema</h3>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: '#0f172a', borderRadius: '8px', border: '1px solid #334155' }}>
              <div>
                <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 500 }}>Sync automático diario</div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>Se ejecuta cada día a las 06:30 Europe/Madrid</div>
              </div>
              <Switch
                data-testid="settings-feature-toggle"
                checked={config.daily_sync_enabled ?? true}
                onCheckedChange={v => set('daily_sync_enabled', v)}
              />
            </div>

            {/* Auto-curación nocturna (heal-gaps) */}
            <div style={{ padding: '14px 16px', background: '#0f172a', borderRadius: '8px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>Auto-curación de huecos nocturna</span>
                    <span style={{ background: '#451a03', color: '#fbbf24', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>NUEVO</span>
                  </div>
                  <div style={{ color: '#64748b', fontSize: '12px' }}>
                    Detecta y rellena automáticamente días sin datos en BD, sin intervención humana
                  </div>
                </div>
                <Switch
                  data-testid="settings-heal-gaps-toggle"
                  checked={config.heal_gaps_enabled ?? true}
                  onCheckedChange={v => set('heal_gaps_enabled', v)}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Hora de ejecución (Madrid)</Label>
                  <Input
                    data-testid="settings-heal-gaps-hour"
                    type="number"
                    min={0}
                    max={23}
                    value={config.heal_gaps_hour ?? 4}
                    onChange={e => set('heal_gaps_hour', Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
                    disabled={config.heal_gaps_enabled === false}
                    style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9' }}
                  />
                  <div style={{ color: '#475569', fontSize: '11px' }}>Default 04:00 (entre el cierre RDS y el sync diario)</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Ventana de inspección (días)</Label>
                  <Input
                    data-testid="settings-heal-gaps-window"
                    type="number"
                    min={1}
                    max={90}
                    value={config.heal_gaps_window_days ?? 14}
                    onChange={e => set('heal_gaps_window_days', Math.max(1, Math.min(90, Number(e.target.value) || 1)))}
                    disabled={config.heal_gaps_enabled === false}
                    style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9' }}
                  />
                  <div style={{ color: '#475569', fontSize: '11px' }}>Default 14 días. Si subes este valor, tarda más pero recupera más histórico</div>
                </div>
              </div>
            </div>

            <BackfillCard />

            <div style={{ padding: '14px 16px', background: '#0f172a', borderRadius: '8px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>Sync casi en tiempo real</span>
                    <span style={{ background: '#451a03', color: '#fbbf24', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>NUEVO</span>
                  </div>
                  <div style={{ color: '#64748b', fontSize: '12px' }}>
                    Sincroniza el día de hoy cada X minutos para que los emails de entrada/salida lleguen poco después del fichaje real (el sync diario solo procesa el día anterior)
                  </div>
                </div>
                <Switch
                  data-testid="settings-hourly-sync-toggle"
                  checked={config.hourly_sync_enabled ?? true}
                  onCheckedChange={v => set('hourly_sync_enabled', v)}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Frecuencia (minutos)</Label>
                  <Input
                    data-testid="settings-hourly-sync-interval"
                    type="number"
                    min={15}
                    max={360}
                    step={15}
                    value={config.hourly_sync_interval_minutes ?? 60}
                    onChange={e => set('hourly_sync_interval_minutes', Math.max(15, Math.min(360, Number(e.target.value) || 60)))}
                    disabled={config.hourly_sync_enabled === false}
                    style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9' }}
                  />
                  <div style={{ color: '#475569', fontSize: '11px' }}>Default 60min. Mínimo 15min para no sobrecargar Mongo/RDS con sincronizaciones solapadas</div>
                </div>
              </div>
            </div>

            <ServerInfoPanel />
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Save button */}
      <div style={{ maxWidth: '700px', marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={save}
          disabled={saving}
          style={{ background: '#2563eb', color: 'white', padding: '0 28px', height: '40px', gap: '8px', display: 'flex', alignItems: 'center' }}
        >
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </div>
    </div>
  );
}
